/**
 * Short times for threads, in the workspace's timezone, as the Family designs
 * write them: "8:40" today, "Yesterday", "Mon" this week, then "Sep 3".
 */
/**
 * The instant's calendar date in `timeZone`, as a day count. Days are counted
 * on the calendar, not in 24-hour steps, so a 23- or 25-hour day across a
 * daylight-saving change is still one day. Built from the numeric parts, not
 * a formatted string: locale output differs between runtimes (slashes, order).
 */
function localDay(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(part("year"), part("month") - 1, part("day")) / 86_400_000;
}

function clockTime(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" })
    .formatToParts(at)
    .filter((p) => p.type === "hour" || p.type === "minute" || p.type === "literal")
    .map((p) => p.value)
    .join("")
    .trim();
}

export function shortTime(iso: string, timeZone: string, now = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const daysAgo = localDay(now, timeZone) - localDay(at, timeZone);
  if (daysAgo <= 0) return clockTime(at, timeZone);
  if (daysAgo === 1) return "Yesterday";
  if (daysAgo < 7)
    return new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(at);
  return new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric" }).format(at);
}

export function replyCountLabel(n: number) {
  return `${n} ${n === 1 ? "reply" : "replies"}`;
}

/** A message's time, and that it came back after a computer restart (C§6.2). */
export function messageTime(
  message: { created_at: string; data: Record<string, unknown> },
  timeZone: string,
): string {
  const at = shortTime(message.created_at, timeZone);
  return message.data.recovered === true ? `${at} · recovered after the restart` : at;
}

/** The agent's replies in a thread: its `text` messages. */
export function replyCount(messages: Array<{ kind: string; author_agent_id: string | null }>) {
  return messages.filter((m) => m.kind === "text" && m.author_agent_id !== null).length;
}
